const STORAGE_KEY = "slideshowMediaCache"
const TTL_MS = 72 * 60 * 60 * 1000
const memoryCache = new Map<string, number>()

type CacheMap = Record<string, number>

function getStorage(): Storage | undefined {
  if (typeof window === "undefined") {
    return
  }

  return window.localStorage
}

function loadCache(storage?: Storage): CacheMap {
  const targetStorage = storage ?? getStorage()
  if (!targetStorage) {
    return Object.fromEntries(memoryCache)
  }

  const raw = targetStorage.getItem(STORAGE_KEY)
  if (!raw) {
    return {}
  }

  const parsed = JSON.parse(raw) as CacheMap

  if (!parsed || typeof parsed !== "object") {
    return {}
  }

  return parsed
}

function saveCache(cache: CacheMap, storage?: Storage) {
  const targetStorage = storage ?? getStorage()

  if (!targetStorage) {
    memoryCache.clear()

    for (const [key, value] of Object.entries(cache)) {
      memoryCache.set(key, value)
    }

    return
  }

  targetStorage.setItem(STORAGE_KEY, JSON.stringify(cache))
}

function pruneCache(cache: CacheMap, now: number, storage?: Storage) {
  let changed = false
  for (const [key, value] of Object.entries(cache)) {
    if (now - value > TTL_MS) {
      delete cache[key]
      changed = true
    }
  }

  if (changed) {
    saveCache(cache, storage)
  }
}

export function useSlideshowMediaCache() {
  function getKey(slideshowId: string, mediaId: string, mediaType?: string) {
    return `${slideshowId}:${mediaType ?? "unknown"}:${mediaId}`
  }

  function has(slideshowId: string, mediaId: string, mediaType?: string) {
    const cacheKey = getKey(slideshowId, mediaId, mediaType)
    const now = Date.now()
    const storage = getStorage()
    const cache = loadCache(storage)
    const cachedAt = cache[cacheKey]

    if (typeof cachedAt !== "number") {
      return false
    }

    if (now - cachedAt > TTL_MS) {
      delete cache[cacheKey]
      saveCache(cache, storage)
      return false
    }

    return true
  }

  function add(slideshowId: string, mediaId: string, mediaType?: string) {
    const cacheKey = getKey(slideshowId, mediaId, mediaType)
    const now = Date.now()
    const storage = getStorage()
    const cache = loadCache(storage)

    cache[cacheKey] = now
    pruneCache(cache, now, storage)
    saveCache(cache, storage)
  }

  function clear() {
    const storage = getStorage()
    saveCache({}, storage)
  }

  return {
    has,
    add,
    clear,
  }
}
